import React, { memo, useCallback, useRef } from 'react'
import { Button, Modal, Skeleton } from 'antd'
import { classNames } from '../../../../../../../lib/classNames/classNames'
import { DefaultJsonForm } from '../../../../../default-json-form'
import { convertData2JsonForm } from '../../../../../../../shared/lib/convertData2JsonForm'
import { schema } from '../../../../../../input-payment-amount/model/schema/modal-schema'
import { isFormDataRequired } from '../../../../../../../../lib/utils/collections'
import { editPayment } from './editPayment'
import { EditOutlined } from '@ant-design/icons'

interface ModalPaymentAmountProps {
  className?: string
  paymentId?: string | number
  sum?: number
  date?: string
}

const { resultSchema, resultObject } = convertData2JsonForm(schema)

export const ModalEditPayment: React.FC<ModalPaymentAmountProps> = memo(
  (props: ModalPaymentAmountProps) => {
    const { className, paymentId, sum, date } = props

    const [isOpen, setIsOpen] = React.useState(false)

    const form = useRef<any>({
      resultSchema,
      resultObject,
    })
    form.current.resultObject.date = date
    form.current.resultObject.sum = sum

    const changeFormData = useCallback(
      async ({ formData }) => {
        const isFilesEdit = JSON.stringify(formData) === JSON.stringify(form.current?.resultObject)
        if (isFilesEdit) {
          return
        }
        form.current.resultObject = { ...formData }
      },
      [form],
    )

    const handleAddPayment = useCallback(async () => {
      if (!form.current || !paymentId) {
        return
      }
      const controller = new AbortController()
      const data = { ...form.current?.resultObject }
      const isRequired = isFormDataRequired(data, form.current?.resultSchema)

      if (!isRequired) {
        Modal.warning({
          title: 'Предупреждение',
          content: 'Заполните все обязательные поля',
        })
        return
      }

      if (data.sum <= 0) {
        Modal.warning({
          title: 'Предупреждение',
          content: 'Сумма платежа не может быть меньше равной 0.',
        })
        return
      }

      await editPayment(paymentId, data, controller.signal)
        .then(() => {
          window.location.reload()
        })
        .catch(err => {
          const message = err?.message || 'При сохранении возникла ошибка'
          Modal.error({
            title: 'Ошибка',
            content: `${message}.\rИзменения не сохранены.`,
          })
        })
    }, [form, paymentId])

    if (!paymentId) {
      return null
    }

    return (
      <>
        <Button
          title="Редактировать платеж"
          type="link"
          icon={<EditOutlined />}
          onClick={() => setIsOpen(true)}
        ></Button>
        <Modal
          title="Редактирование платежа"
          open={isOpen}
          onOk={handleAddPayment}
          onCancel={() => setIsOpen(false)}
          className={classNames('modal-payment-amount', {}, [className])}
        >
          {form.current ? (
            <DefaultJsonForm
              formData={{ ...form.current.resultObject }}
              schema={form.current.resultSchema}
              onChange={changeFormData}
              orientation="horizontal"
            />
          ) : (
            <Skeleton active />
          )}
        </Modal>
      </>
    )
  },
)
ModalEditPayment.displayName = 'ModalEditPayment'
